<template>
  <div class="content overlay__inner" :class="formClass">
    <div class="heading-bar">
      <div class="heading-bar__actions">
        <button type="button" class="close-icon" @click="close">&times;</button>
      </div>
      <h1 v-if="!editMode" class="hdg hdg--3">Add availability</h1>
      <h1 v-else class="hdg hdg--3">Edit availability</h1>
    </div>

    <form @submit.prevent="submitForm">
      <div class="form__section">
        <div class="form__group">
          <div class="form__group-item">
            <poppins-date-picker
              id="form-date"
              v-model="sittingDate"
              label-name="Available date"
              :max-date="maxStartDate"
              :status="sittingDateStatus"
              :helper-text="sittingDateHelperText"
              :custom-classes="'w-full'"
              @manual-input="validateTypedInput"
            />
          </div>
        </div>

        <div class="form__group">
          <div class="form__group-item form__group-item--col form__group-item--mb-3">
            <poppins-time-picker
              id="form-start-date"
              v-model="startTime"
              label-name="Start time"
              :start="startRange[0]"
              :end="startRange[1]"
              :status="startTimeStatus"
              :helper-text="startTimeHelperText"
            />
          </div>

          <div class="form__group-item form__group-item--col form__group-item--mb-3">
            <poppins-time-picker
              id="regular-schedule-end"
              v-model="endTime"
              label-name="End time"
              :start="endRange[0]"
              :end="endRange[1]"
              :status="endTimeStatus"
              :helper-text="endTimeHelperText"
            />
          </div>
        </div>
      </div>

      <div v-if="!editMode" class="form__repeat-group">
        <poppins-checkbox
          v-model="repeating"
          value="repeat"
          checkbox-id="repeat"
          label-name="Repeat this time block"
          label-class="mb-0"
        />
      </div>

      <div v-if="!editMode && repeating" class="form__repeating">
        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_weekly"
            v-model="repeatOn"
            radio-name="repeat_on"
            :label-name="`Weekly on ${repeatDay}`"
            radio-value="weekly"
          />
        </div>

        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_daily"
            v-model="repeatOn"
            :disabled="!showEveryWeekday || null"
            radio-name="repeat_on"
            label-name="Every weekday (Mon to Fri)"
            radio-value="weekdays"
          />
          <p v-if="errors['repetition']" class="form__error">{{ errors['repetition'] }}</p>
        </div>

        <div class="form__repeat-group">
          <p class="form__notice">This will be repeated for about 90 days.</p>
        </div>
      </div>

      <div class="form__section form__section--flex-buttons">
        <Button v-if="editMode" value="Delete" variant="outlined" size="medium" class="w-full" @click.prevent="clear" />
        <Button value="Save" variant="primary" size="medium" type="submit" class="w-full" />
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { add, addDays, addMinutes, endOfDay, format, intervalToDuration, isValid, parseISO, set, startOfDay } from 'date-fns';
import helperStatus from '@/helpers/status';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import Button from '@/components/button/Button.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import RadioButton from '@/components/radio_button/RadioButton.vue';

export default {
  name: 'AvailabilityForm',
  components: {
    PoppinsDatePicker,
    PoppinsTimePicker,
    Button,
    PoppinsCheckbox: Checkbox,
    PoppinsRadioButton: RadioButton,
  },
  props: {
    availability: {
      type: Object,
      default: null,
    },
    formClass: {
      type: String,
      default: '',
    },
  },
  emits: ['submit', 'submitRepeating', 'update', 'clear', 'close'],
  data(): {
    errors: any;
    sittingDate: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    startRange: Date[];
    endRange: Date[];
    originalClearStart: string | null;
    originalClearEnd: string | null;
    repeating: boolean;
    repeatOn: string;
    sittingDateHelperText: string;
    endTimeHelperText: string;
    startTimeHelperText: string;
  } {
    const start = this.availability?.node?.start ?? null;
    const end = this.availability?.node?.end ?? null;

    return {
      errors: {},
      sittingDate: start ? startOfDay(parseISO(start)) : addDays(new Date(), 2),
      startTime: start ? parseISO(start) : null,
      endTime: end ? parseISO(end) : null,
      startRange: [],
      endRange: [],
      originalClearStart: start,
      originalClearEnd: end,
      repeating: false,
      repeatOn: 'weekly',
      sittingDateHelperText: '',
      endTimeHelperText: '',
      startTimeHelperText: '',
    };
  },
  computed: {
    maxStartDate(): Date {
      const today = new Date();
      const thirtyDaysFromToday = new Date();
      // returning the code below will return a number, don't do it, you need to return a date
      thirtyDaysFromToday.setDate(today.getDate() + 30);

      // returning the variable like this will return a date
      return thirtyDaysFromToday;
    },
    editMode(): boolean {
      return !!this.availability;
    },
    sittingDateStatus(): helperStatus {
      if (this.sittingDateHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    startTimeStatus(): helperStatus {
      if (this.startTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    endTimeStatus(): helperStatus {
      if (this.endTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    repeatDay(): string {
      if (!this.sittingDate) return '';

      return format(this.sittingDate, 'eee');
    },
    showEveryWeekday(): boolean {
      if (!this.sittingDate) return false;

      const d = this.sittingDate.getDay();
      return d > 0 && d < 6;
    },
  },
  watch: {
    sittingDate(value: Date | null) {
      this.sittingDateHelperText = '';

      if (value === null) {
        this.startTime = null;
        this.endTime = null;
        this.calculateStartRange();
        this.calculateEndRange();
        return;
      }

      if (this.startTime) {
        const oldStart = this.startTime;
        this.startTime = set(this.startTime, {
          year: value.getFullYear(),
          month: value.getMonth(),
          date: value.getDate(),
        });

        // keep the original interval by shifting the end time along with the start
        if (this.endTime) {
          const interval = intervalToDuration({ start: oldStart, end: this.endTime });
          this.endTime = add(this.startTime, interval);
        }
      }

      this.calculateStartRange();
    },
    startTime(newStart: Date | null, oldStart: Date | null) {
      this.startTimeHelperText = '';

      if (newStart && newStart.getTime() !== oldStart?.getTime()) {
        this.calculateEndRange();
      }
    },
    endTime() {
      this.endTimeHelperText = '';
    },
  },
  created(): void {
    this.calculateStartRange();
    this.calculateEndRange();
  },
  methods: {
    calculateStartRange(): void {
      if (!this.sittingDate) {
        this.startRange = [];
        return;
      }

      this.startRange = [startOfDay(this.sittingDate), endOfDay(this.sittingDate)];
    },
    calculateEndRange(): void {
      if (!this.startTime) {
        this.endRange = [];
        return;
      }

      // Always refresh the range from the current start so the end options never go
      // stale. The chosen endTime is kept as-is (PoppinsTimePicker keeps it visible
      // even when it falls outside this range), and validate() flags end <= start on save.
      this.endRange = [addMinutes(this.startTime, 30), addDays(this.startTime, 1)];
    },
    submitForm() {
      if (!this.validate()) return;

      if (this.availability) {
        this.$emit(
          'update',
          { clearStart: this.originalClearStart, clearEnd: this.originalClearEnd },
          this.startTime,
          this.endTime
        );
      } else if (this.repeating) {
        this.$emit('submitRepeating', this.startTime, this.endTime, this.repeatOn.toUpperCase());
      } else {
        this.$emit('submit', this.startTime, this.endTime);
      }
    },
    clear() {
      this.$emit('clear', this.availability);
    },
    // TODO: There will definitely be a better way of doing validation.
    // TODO: We will need to check with product about the correct messages.
    validate(): boolean {
      const e: any = {};

      if (!this.sittingDate) this.sittingDateHelperText = 'This field is required';
      if (!isValid(this.startTime)) this.startTimeHelperText = 'This field is required';
      if (!isValid(this.endTime)) this.endTimeHelperText = 'This field is required';

      if (this.endTime && this.startTime && this.endTime <= this.startTime) {
        this.endTimeHelperText = 'Choose an end time later than the start time.';
      }

      if (!this.showEveryWeekday && this.repeatOn === 'weekdays') e.repetition = 'Weekday repetition cannot be used on a weekend';

      this.errors = e;

      if (this.sittingDateHelperText || this.startTimeHelperText || this.endTimeHelperText) return false;
      if (Object.keys(e).length !== 0) return false;

      return true;
    },
    validateTypedInput(dateString): void {
      const date = new Date(dateString);
      const today = new Date();

      if (date < today || date > this.maxStartDate) {
        this.sittingDate = null;
        this.sittingDateHelperText = 'Please enter a date within a month of today.';
        return;
      }

      this.sittingDate = date;
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
